import { useMutation, useQuery } from "@tanstack/react-query";
import { IAssignmentSubmitPayload } from "../types/assignment.type";
import { ISaveAnswerPayload, studentService } from "../services";
import { useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "@quest/hooks";

const PREVIEW_REJECT = () => Promise.reject(new Error("Read-only preview"));

export const useDraftAssignment = () => {
  const isPreviewMode = useAppSelector((state) => Boolean(state.preview?.active));
  return useMutation({
    mutationKey: ["draft-assignment"],
    mutationFn: (data: IAssignmentSubmitPayload) =>
      isPreviewMode ? PREVIEW_REJECT() : studentService.draftAssignment(data),
  });
};

export const useCompleteAssignment = () => {
  const isPreviewMode = useAppSelector((state) => Boolean(state.preview?.active));
  return useMutation({
    mutationKey: ["complete-assignment"],
    mutationFn: (data: IAssignmentSubmitPayload) =>
      isPreviewMode
        ? PREVIEW_REJECT()
        : studentService.completeAssignment(data),
  });
};

export const useAddComment = () => {
  // const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-comment"],
    mutationFn: (data: { comment: string | null; answer_id: number }) =>
      studentService.addComment(data.comment || null, data.answer_id),
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: ["student-assignment-by-id"] });
    // },
  });
};

export const useMarkAsIsCorrect = () => {
  return useMutation({
    mutationKey: ["mark-as-is-correct"],
    mutationFn: ({
      answer_id,
      is_correct,
    }: {
      answer_id: number;
      is_correct: boolean;
    }) => studentService.markAsIsCorrect(answer_id, is_correct),
  });
};

export const useAcceptAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["accept-assignment"],
    mutationFn: (data: { attempt_id: number; awarded_points?: number }) =>
      studentService.acceptAssignment(data.attempt_id, data.awarded_points),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-assignments"] });
    },
  });
};

export const useRejectAssignment = () => {
  return useMutation({
    mutationKey: ["reject-assignment"],
    mutationFn: (data: { attempt_id: number }) =>
      studentService.rejectAssignment(data.attempt_id),
  });
};

export const useCheckStudentIcaresTestStatus = (
  studentId: number | null,
  schoolYear: number | null
) => {
  return useQuery({
    queryKey: ["check-student-icares-test-status", studentId, schoolYear],
    queryFn: () =>
      studentService.checkStudentIcaresTestStatus(studentId, schoolYear),
    enabled: !!studentId && !!schoolYear && schoolYear > 0 && studentId > 0,
  });
};

export const useSkipTutorial = () => {
  const isPreviewMode = useAppSelector((state) => Boolean(state.preview?.active));
  return useMutation({
    mutationKey: ["skip-tutorial"],
    mutationFn: (idpId: string) =>
      isPreviewMode ? PREVIEW_REJECT() : studentService.skipTutorial(idpId),
  });
};

export const useSaveAnswer = () => {
  const isPreviewMode = useAppSelector((state) => Boolean(state.preview?.active));
  return useMutation({
    mutationKey: ["save-answer"],
    mutationFn: (data: ISaveAnswerPayload) =>
      isPreviewMode ? PREVIEW_REJECT() : studentService.saveAnswer(data),
  });
};
